/**
 * Typed search client for Round Recon.
 *
 * Wraps the `/api/search` and `/api/taxonomy/*` endpoints behind a
 * narrow, typed surface so HomePage, SearchResults, and ProductDetail
 * can share the same fetcher (and the same React Query keys).
 *
 * The shapes here mirror spec §20 (search response) and the
 * `/api/taxonomy/{category}` envelope returned by
 * `api/src/routes/taxonomy.js`.
 */

import { api } from './api';

// ── Listing / search shapes (spec §20) ─────────────────────────────────────

export type StockStatus = 'in_stock' | 'out_of_stock' | 'limited' | 'unknown';
export type DataFreshness = 'fresh' | 'aging' | 'stale' | 'unavailable';

export interface SearchHitRetailer {
  id: string | null;
  name: string | null;
  slug?: string | null;
  trustScore?: number | null;
}

export interface SearchHitAttributes {
  caliber?: string | null;
  /** Taxonomy-derived ammo category (handgun/rifle/rimfire/shotgun) — #233. */
  ammoType?: string | string[] | null;
  brand?: string | null;
  productLine?: string | null;
  bulletWeightGrains?: number | null;
  bulletType?: string | null;
  caseMaterial?: string | null;
  useCases?: string[];
  isReloaded?: boolean | null;
  isSubsonic?: boolean | null;
  isLeadFree?: boolean | null;
}

export interface SearchHit {
  listingId: string;
  productId: string | null;
  productSlug?: string | null;
  title: string | null;
  retailer: SearchHitRetailer;
  price: number | null;
  pricePerRound: number | null;
  roundCount: number | null;
  stockStatus: StockStatus;
  stockQuantity?: number | null;
  lastUpdated?: string | null;
  attributes: SearchHitAttributes;
  isSponsored: boolean;
  /**
   * True when this hit is pinned to the top of results because its retailer is
   * flagged "promoted" (#149, spec §23). Distinct from `isSponsored` (a paid
   * placement). Promoted hits carry a visible + programmatic "Promoted" label.
   */
  isPromoted?: boolean;
  dataFreshness: DataFreshness;
  confidenceScore?: number | null;
  rankingScore?: number | null;
  visitUrl: string;
  sourceUrl?: string | null;
  lotNumber?: string | null;
}

export interface FacetValue {
  value: string;
  count: number;
}

export type SearchFacets = Record<string, FacetValue[]>;

export interface SuggestedFilters {
  caliber?: string[];
  bulletType?: string[];
  bulletWeightGrains?: number;
  useCases?: string[];
}

export interface SearchResponse {
  query: string;
  totalResults: number;
  page: number;
  limit: number;
  results: SearchHit[];
  facets: SearchFacets;
  suggestedFilters: SuggestedFilters;
  /** Count of paid sponsored hits pinned at the top of `results`. */
  sponsoredCount?: number;
  /** Count of promoted-retailer hits pinned after the sponsored slots (#149). */
  promotedCount?: number;
  /**
   * ISO timestamp of the most recently verified listing across the whole
   * matching set (not just this page), for the "last updated" notice (#290).
   */
  mostRecentUpdate?: string | null;
}

/** Response of `GET /api/search/deals` (#153). */
export interface DealsResponse {
  deals: SearchHit[];
}

export type SearchSort = 'relevance' | 'pricePerRound' | 'newest' | 'roundCount' | 'retailerTrust';

export interface SearchParams {
  q?: string;
  page?: number;
  limit?: number;
  sort?: SearchSort;
  /** Sort direction. Defaults to the natural direction for each sort key. */
  sortDir?: 'asc' | 'desc';
  caliber?: string | string[];
  ammoType?: string | string[];
  brand?: string | string[];
  productLine?: string | string[];
  bulletType?: string | string[];
  bulletWeightGrains?: string | string[];
  caseMaterial?: string | string[];
  retailerId?: string | string[];
  useCases?: string | string[];
  minPricePerRound?: number;
  maxPricePerRound?: number;
  minRoundCount?: number;
  stockStatus?: StockStatus;
  inStockOnly?: boolean;
  excludeRemanufactured?: boolean;
  lotNumber?: string;
  excludedBrands?: string | string[];
  excludedRetailers?: string | string[];
  preferredBrands?: string | string[];
  preferredRetailers?: string | string[];
}

// ── Taxonomy shapes ────────────────────────────────────────────────────────

export interface TaxonomyTerm {
  canonical: string;
  aliases?: string[];
}

interface TaxonomyEnvelopeData {
  category: string;
  terms: TaxonomyTerm[];
}

export type TaxonomyCategory =
  | 'calibers'
  | 'use-cases'
  | 'bullet-types'
  | 'case-materials'
  | 'brands';

// ── Helpers ────────────────────────────────────────────────────────────────

type Primitive = string | number | boolean;

function appendParam(parts: string[], key: string, value: Primitive): void {
  parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
}

/**
 * Serialize a flat params object to a query string. Arrays are emitted
 * as repeated `key=` pairs (the backend accepts both repeated keys and
 * CSV — repeated is unambiguous so we use that).
 */
export function toQueryString(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const item of value) {
        if (item === undefined || item === null || item === '') continue;
        appendParam(parts, key, item as Primitive);
      }
    } else {
      appendParam(parts, key, value as Primitive);
    }
  }
  return parts.length === 0 ? '' : `?${parts.join('&')}`;
}

// ── Public API ─────────────────────────────────────────────────────────────

/**
 * Execute a search against `GET /api/search`. Returns the §20-shaped
 * envelope unwrapped (the api wrapper already strips the REST-SPEC
 * envelope's `data` field).
 */
export function searchListings(params: SearchParams = {}): Promise<SearchResponse> {
  return api.get<SearchResponse>(`/search${toQueryString(params as Record<string, unknown>)}`);
}

/**
 * Fetch the "best current deals" cross-section — the single cheapest in-stock
 * listing per top caliber — in ONE request (`GET /api/search/deals`, #153).
 * Replaces the homepage's N parallel per-caliber queries; the response is
 * identical for every anonymous visitor, so it's served from a shared/browser
 * cache and barely touches the API rate limit.
 */
export function getDeals(): Promise<DealsResponse> {
  return api.get<DealsResponse>('/search/deals');
}

/**
 * Fetch a taxonomy category. `category` is the URL slug used by
 * `api/src/routes/taxonomy.js` (e.g. `calibers`, `bullet-types`).
 */
export async function getTaxonomy(category: TaxonomyCategory): Promise<TaxonomyTerm[]> {
  const data = await api.get<TaxonomyEnvelopeData>(`/taxonomy/${category}`);
  return Array.isArray(data?.terms) ? data.terms : [];
}

/**
 * Fetch caliber facet counts for in-stock listings.
 *
 * Used by the "Shop by Caliber" section to rank calibers by inventory depth
 * (spec §15.2, issue #2 criterion 2). The result is the `facets.caliber`
 * array from a zero-result search with `inStockOnly=true`, which Meilisearch
 * returns without transferring any hit payloads (`limit=0`).
 *
 * Ordering criteria (spec §15.2, issue #2):
 *   1. TODO(#2): Most commonly searched — wire this once `SearchQuery` logging
 *      (spec §13.1) is implemented. When search-volume data is available,
 *      sort `FacetValue[]` by descending search-volume count first, then fall
 *      back to inventory count as a tiebreaker.
 *   2. Most commonly found in inventory — current implementation (inventory
 *      count descending from `facets.caliber`).
 *
 * @returns Array of `FacetValue` sorted by in-stock listing count descending,
 *          or an empty array when the search index is unavailable.
 */
export async function getCaliberInventoryCounts(): Promise<FacetValue[]> {
  const response = await searchListings({ inStockOnly: true, limit: 0 });
  const facets = response?.facets?.caliber;
  if (!Array.isArray(facets) || facets.length === 0) return [];
  // Meilisearch already returns facets sorted by count descending, but we
  // sort explicitly here to guarantee the contract regardless of backend
  // implementation drift.
  return [...facets].sort((a, b) => b.count - a.count);
}

export const searchApi = {
  searchListings,
  getDeals,
  getTaxonomy,
  getCaliberInventoryCounts,
  toQueryString,
};

export default searchApi;
